import React, { type ReactNode, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSend } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { Avatar } from '@components/basics/Images/Avatar';
import { Spinner } from '@components/basics/Spinner';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';

type PostCreator = {
  id: string;
  username: string;
  publicAddress: string;
  webappUri: string;
  artist: {
    id: string;
  } | null;
  avatar: {
    id: string;
    url: string;
  } | null;
};

const AVATAR_SIZE = 40;

export function ReplyPostContent({
  isLoading,
  postCreator,
  postContent,
  onSubmit,
  isSubmitting,
}: {
  isLoading: boolean;
  postCreator: PostCreator | undefined;
  postContent: ReactNode | undefined;
  onSubmit: (content: string) => void;
  isSubmitting: boolean;
}) {
  const [text, setText] = useState('');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex h-[12] max-h-[48] w-full gap-3">
        {postCreator && !isLoading ? (
          <div className="h-10 w-10 flex-shrink-0">
            <Avatar
              size={AVATAR_SIZE}
              walletAddress={postCreator.publicAddress}
              webappUri={postCreator.webappUri}
              src={postCreator.avatar?.url}
              username={postCreator.username}
              borderRadius={AVATAR_SIZE}
            />
          </div>
        ) : (
          <div className="h-10 w-10 animate-pulse rounded-full bg-neutral200" />
        )}

        <div className="flex w-full flex-col gap-1.5">
          <div className="flex items-center gap-1">
            {postCreator && !isLoading ? (
              <>
                <span className="font-base text-base-m font-semibold text-base900">
                  {postCreator.username}
                </span>{' '}
                {!!postCreator.artist && <VerifiedTooltip size={14} portal noTooltip />}
              </>
            ) : (
              <div className="h-5 w-48 animate-pulse rounded-sm bg-neutral200" />
            )}
          </div>

          {postCreator && !isLoading ? (
            <div className="line-clamp-4">{postContent}</div>
          ) : (
            <div className="flex w-full flex-col gap-2">
              <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
              <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
            </div>
          )}
        </div>
      </div>

      {postCreator && postContent && !isLoading ? (
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmit(text);
          }}
          className="relative flex h-10 w-full items-center gap-3 rounded-md bg-base100 px-3"
        >
          <input
            disabled={isSubmitting}
            autoFocus
            className="w-full bg-transparent font-base text-base-m text-base900 outline-none placeholder:text-neutral500"
            type="text"
            placeholder={`Replying to ${postCreator.username}`}
            onChange={e => setText(e.target.value)}
            value={text}
          />
          {isSubmitting ? (
            <div className="bottom-0 right-0 top-0 flex items-center justify-center">
              <Spinner size="sm" className=" text-neutral300" />
            </div>
          ) : (
            <button
              className={`bottom-0 right-0 flex aspect-square h-7 w-7 items-center justify-center  rounded-full ${
                text.length ? `bg-base800 hover:bg-base800/90` : `bg-base800/30`
              }`}
              type="submit"
            >
              <FontAwesomeIcon icon={faSend} size="sm" className="text-white" />
            </button>
          )}
        </form>
      ) : (
        <div className="h-10 w-full animate-pulse rounded-sm bg-neutral200" />
      )}
    </div>
  );
}
